import { useEffect, useState, type RefObject } from "react";
import { scrollRootOf } from "../components/ui";

/** How far below the frozen page header a heading still counts as "reached". */
const SLACK_PX = 24;

/**
 * Which section the reader is in, for an "On this page" rail: the last of
 * `ids` whose heading has scrolled up to the frozen page header
 * (`--page-bar`, published by PageHeader). Follows the scroll both ways -
 * the hash only says what was last clicked, so a rail driven by it stood
 * still while the reader scrolled back up (#410; ported verbatim from
 * Mermera, txp-labs/mermera-attestation-app#933, templates decision 7).
 *
 * **It measures the scroll container the page actually scrolls in**: the
 * nearest `[data-scroll-root]`, or the document when there is none -
 * Starsach's signed-in shell scrolls the document. Mermera's first version
 * listened to the window while its shell scrolled <main>, so its rail never
 * moved and its "at the bottom" test compared the page with the window -
 * always true - so every page opened with its last section marked.
 *
 * At the top it is the first id. A page that has been scrolled to the very
 * bottom marks the last, so a short final section can still become current
 * even though its heading never reaches the top - unless `hash` names a
 * section that is on screen there: a click on a late entry scrolls only as
 * far as the page allows, and the entry clicked must be the one marked. A
 * page too short to scroll stays on its first section.
 */
export function useActiveSection(ids: readonly string[], hash = ""): string | undefined {
  const [active, setActive] = useState<string | undefined>(ids[0]);
  const key = ids.join("\n");

  useEffect(() => {
    const list = key ? key.split("\n") : [];
    if (list.length === 0) return;
    const root = scrollRootOf(document.getElementById(list[0]!));
    const isDocument = root === document.documentElement;
    // Scroll events come from the window when the document scrolls.
    const target: HTMLElement | Window = isDocument ? window : root;

    let frame = 0;
    const measure = () => {
      frame = 0;
      const elements = list
        .map((id) => document.getElementById(id))
        .filter((e): e is HTMLElement => e !== null);
      if (elements.length === 0) return;
      const rootTop = isDocument ? 0 : root.getBoundingClientRect().top;
      const viewHeight = isDocument ? window.innerHeight : root.clientHeight;
      const bar = parseFloat(getComputedStyle(elements[0]!).getPropertyValue("--page-bar")) || 0;
      const line = bar + SLACK_PX;
      const atBottom = root.scrollTop > 0 && root.scrollTop + viewHeight >= root.scrollHeight - 2;
      const topOf = (e: HTMLElement) => e.getBoundingClientRect().top - rootTop;
      const clicked = elements.find((e) => `#${e.id}` === hash);
      const clickedTop = clicked ? topOf(clicked) : undefined;
      let current = elements[0]!.id;
      if (clicked && clickedTop !== undefined && clickedTop >= 0 && clickedTop <= line) {
        // The heading just jumped to sits at the top: it is what the reader
        // asked for, even when a short section above it is also in the band.
        current = clicked.id;
      } else if (atBottom) {
        current =
          clicked && clickedTop !== undefined && clickedTop >= 0 && clickedTop < viewHeight
            ? clicked.id
            : elements[elements.length - 1]!.id;
      } else
        for (const e of elements) {
          if (topOf(e) <= line) current = e.id;
          else break;
        }
      setActive(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    target.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      target.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [key, hash]);

  // The filter can take the marked section off the page before the next
  // scroll measures again; fall back to the first one still there.
  return active !== undefined && ids.includes(active) ? active : ids[0];
}

/**
 * Keeps the marked entry of a rail that scrolls inside itself in sight:
 * the release notes list every version, more than a screen, and a reader
 * deep in the page would otherwise see no entry marked at all. Moves only
 * the rail's own scroll, never the page's.
 */
export function useRailFollows(rail: RefObject<HTMLElement | null>, active: string | undefined): void {
  useEffect(() => {
    const nav = rail.current;
    const entry = nav?.querySelector('[aria-current="location"]');
    if (!nav || !entry || nav.scrollHeight <= nav.clientHeight) return;
    const box = nav.getBoundingClientRect();
    const at = entry.getBoundingClientRect();
    if (at.top < box.top) nav.scrollTop -= box.top - at.top;
    else if (at.bottom > box.bottom) nav.scrollTop += at.bottom - box.bottom;
  }, [rail, active]);
}
